import { valibotResolver } from '@hookform/resolvers/valibot'
import { useRouter } from '@tanstack/react-router'
import { Form, Input } from 'antd'
import { type FC, type FormEvent, useCallback, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { FaCheckCircle } from 'react-icons/fa'

import {
  BasicTable,
  BasicTbody,
  BasicTd,
  BasicTh,
  BasicTr,
  Button,
  ModalBody,
  ModalFooter,
  RequiredTag
} from '@/components/elements'
import {
  ACTIONS,
  ERROR_MESSAGES,
  LABELS,
  SUCCESS_MESSAGES
} from '@/constants/common/message.constants'
import { PartnerSearchSelect } from '@/features/partners/components'
import { usePartner } from '@/features/partners/hooks'
import { toastApiErrorMessage } from '@/features/shared/utils'
import {
  type CreatePartnerToSubPartnerDto,
  type PartnerToSubPartnerEntity
} from '@/libs/api'
import { CreatePartnerToSubPartnerDtoSchema } from '@/routes/_protected/$tenantKey/settings/partner-to-sub-partners/-modals/'

import { createPartnerToSubPartner } from '../../api'

type Props = {
  tenantKey: string
  partnerId: number
  onSuccess: (
    partnerToSubPartnerEntity: PartnerToSubPartnerEntity
  ) => Promise<void>
  onCancel: () => void
}

export const SubPartnerCreateModalContent: FC<Props> = ({
  tenantKey,
  partnerId,
  onSuccess,
  onCancel
}) => {
  const {
    control,
    setFocus,
    handleSubmit,
    formState: { isSubmitting, isValid }
  } = useForm<CreatePartnerToSubPartnerDto>({
    defaultValues: {
      partnerId,
      subPartner: {
        name: ''
      }
    },
    resolver: valibotResolver(CreatePartnerToSubPartnerDtoSchema)
  })
  const router = useRouter()
  const { dcId } = usePartner({ tenantKey, partnerId })

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      // formが入れ子になっている場合の対応
      // - submitイベントの親要素への伝播を止める
      if (event) {
        if (typeof event.preventDefault === 'function') {
          event.preventDefault()
        }
        if (typeof event.stopPropagation === 'function') {
          event.stopPropagation()
        }
      }
      return handleSubmit(async (formValue) => {
        try {
          const { data } = await createPartnerToSubPartner({
            tenantKey,
            dto: {
              ...formValue,
              dcId
            }
          })
          onSuccess(data)
          toast.success(SUCCESS_MESSAGES.REGISTERED_SUB_PARTNER)
        } catch (error) {
          toastApiErrorMessage(
            error,
            ERROR_MESSAGES.FAILED_TO_REGISTER_SUB_PARTNER
          )
        }
      })(event)
    },
    [tenantKey, onSuccess, handleSubmit, dcId]
  )

  useEffect(() => {
    const unsubscribe = router.subscribe('onResolved', () => {
      onCancel()
    })
    return () => {
      unsubscribe()
    }
  }, [router, onCancel])

  useEffect(() => {
    setFocus('subPartner.name')
  }, [setFocus])

  return (
    <form onSubmit={onSubmit}>
      <ModalBody>
        <BasicTable className="w-full table-fixed">
          <colgroup>
            <col className="w-[150px]" />
            <col />
          </colgroup>
          <BasicTbody>
            <BasicTr>
              <BasicTh className="align-top">
                <label htmlFor="code">
                  {LABELS.PARTNER_DELIVERY}
                  <RequiredTag className="ml-2" />
                </label>
              </BasicTh>
              <BasicTd className="w-full align-top">
                <Controller
                  name="partnerId"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <PartnerSearchSelect
                        {...field}
                        tenantKey={tenantKey}
                        value={partnerId}
                        dcIds={[dcId]}
                        id={field.name}
                        disabled
                      />
                    </Form.Item>
                  )}
                />
              </BasicTd>
            </BasicTr>
            <BasicTr>
              <BasicTh className="align-top">
                <label htmlFor="code">
                  {LABELS.ACTUAL_SUB_PARTNER_NAME}
                  <RequiredTag className="ml-2" />
                </label>
              </BasicTh>
              <BasicTd colSpan={3} className="align-top">
                <Controller
                  name="subPartner.name"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <Input
                        {...field}
                        placeholder={LABELS.ACTUAL_SUB_PARTNER_NAME}
                        className="w-full"
                        id={field.name}
                      />
                    </Form.Item>
                  )}
                />
              </BasicTd>
            </BasicTr>
          </BasicTbody>
        </BasicTable>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onCancel}>{ACTIONS.CANCEL}</Button>
        <Button
          type="primary"
          icon={<FaCheckCircle />}
          htmlType="submit"
          disabled={!isValid}
          loading={isSubmitting}
        >
          {ACTIONS.REGISTER}
        </Button>
      </ModalFooter>
    </form>
  )
}
